/** Key Results — create form (RHF + zod + shadcn Form via Refine useForm). */
import { useForm } from "@refinedev/react-hook-form";
import { useList } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import type { Control, FieldValues, Path } from "react-hook-form";

import {
  createKeyResultSchema,
  ENTITY_STATUS,
  type CreateKeyResultInput,
  type Objective,
} from "@aos/shared";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { PageHeader } from "../projects/_shared";
import { FormGrid, SelectField, TextField } from "../projects/_form";

/** Coerce an empty number input to undefined; otherwise to a finite number. */
function numberOrUndefined(v: string): number | undefined {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Numeric field that coerces an empty input to `undefined` (never submits NaN —
 * design-system §4.5). Inline because the shared `TextField` doesn't take a
 * `setValueAs` and the shared `_form.tsx` is a primitive we must not edit.
 */
function NumberField<TValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
}: {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
  placeholder?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              step="any"
              placeholder={placeholder}
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              value={field.value ?? ""}
              onChange={(e) => field.onChange(numberOrUndefined(e.target.value))}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** KeyResultCreate (D6) — create form backed by `createKeyResultSchema`. */
export function KeyResultCreate() {
  const { data: objData } = useList<Objective>({
    resource: "objectives",
    pagination: { pageSize: 200 },
  });
  const objectives = objData?.data ?? [];

  const form = useForm<CreateKeyResultInput, any, CreateKeyResultInput>({
    resolver: zodResolver(createKeyResultSchema),
    refineCoreProps: { resource: "key-results", action: "create" },
    defaultValues: { status: "active" },
  });

  const {
    refineCore: { formLoading },
    saveButtonProps,
    control,
  } = form;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="New Key Result"
        description="Add a measurable signal tracking progress toward an objective."
      />
      <Form {...form}>
        <form onSubmit={saveButtonProps.onClick}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Key result details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={control}
                name="objective_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Objective</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={(field.value as string | undefined) ?? undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an objective…" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {objectives.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <TextField
                control={control}
                name="title"
                label="Title"
                placeholder="Pricing signals captured per week"
              />

              <FormGrid>
                <NumberField
                  control={control}
                  name="current_value"
                  label="Current value"
                />
                <NumberField
                  control={control}
                  name="target_value"
                  label="Target value"
                />
                <TextField
                  control={control}
                  name="unit"
                  label="Unit"
                  placeholder="%, $, count…"
                />
                <SelectField
                  control={control}
                  name="status"
                  label="Status"
                  options={ENTITY_STATUS}
                />
              </FormGrid>
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" disabled={formLoading}>
                {formLoading ? "Creating…" : "Create Key Result"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}

export default KeyResultCreate;
